import React from 'react';
import { Button, Select } from 'antd';
import { PlusOutlined, DeleteOutlined } from '@ant-design/icons';
import type { FD111 } from '../../../services/d211Api';
import { dateToInput, inputToDate, fmt } from '../helpers';

interface Tab2Props {
  details: Partial<FD111>[];
  selectedIdx: number;
  isEditing: boolean;
  isApproved: boolean;
  onSelectRow: (idx: number) => void;
  onAddRow: () => void;
  onDeleteRow: (idx: number) => void;
  onDetailChange: (idx: number, key: string, value: unknown) => void;
}

const Tab2Detail: React.FC<Tab2Props> = React.memo(({
  details, selectedIdx, isEditing, isApproved,
  onSelectRow, onAddRow, onDeleteRow, onDetailChange,
}) => {
  const selected = selectedIdx >= 0 && selectedIdx < details.length ? details[selectedIdx] : null;

  return (
    <div className="d211-tab-content">
      {/* Buttons */}
      <div className="detail-toolbar">
        <Button icon={<PlusOutlined />} onClick={onAddRow}
          disabled={!isEditing || isApproved} size="small">
          明細新增
        </Button>
        <Button icon={<DeleteOutlined />} onClick={() => onDeleteRow(selectedIdx)}
          disabled={!isEditing || selectedIdx < 0} size="small" danger>
          明細刪除
        </Button>
        <span className="detail-count">共 {details.length} 筆 (總項數: {details.length})</span>
      </div>

      {/* Grid */}
      <div className="detail-grid-wrapper">
        <table className="detail-grid">
          <thead>
            <tr>
              <th>項次</th>
              <th>產品代號</th>
              <th>預定交期</th>
              <th>採購數量</th>
              <th>單位</th>
              <th>採購單價</th>
              <th>採購金額</th>
              <th>允收量</th>
              <th>應付單價</th>
              <th>折讓單價</th>
            </tr>
          </thead>
          <tbody>
            {details.map((d, i) => (
              <tr key={i} className={i === selectedIdx ? 'row-selected' : ''}
                onClick={() => onSelectRow(i)}>
                <td>{d.fd111_02 ?? i + 1}</td>
                <td>{(d.fd111_10 || '') + (d.fd111_11 || '')}</td>
                <td>{dateToInput(d.fd111_19)}</td>
                <td className="text-right">{fmt(d.fd111_15)}</td>
                <td>{d.fd111_16 || ''}</td>
                <td className="text-right">{fmt(d.fd111_17)}</td>
                <td className="text-right">{fmt(d.fd111_18)}</td>
                <td className="text-right">{fmt(d.fd111_30)}</td>
                <td className="text-right">{fmt(d.fd111_26)}</td>
                <td className="text-right">{fmt(d.fd111_25)}</td>
              </tr>
            ))}
            {details.length === 0 && (
              <tr><td colSpan={10} className="text-center text-muted">無明細資料</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Detail panel (below grid) */}
      {selected && (
        <div className="detail-panel">
          <div className="form-grid">
            <label>產品代號(前)</label>
            <input className="form-input" value={selected.fd111_10 || ''}
              onChange={e => onDetailChange(selectedIdx, 'fd111_10', e.target.value)}
              disabled={!isEditing} />
            <label>產品代號(後)</label>
            <input className="form-input" value={selected.fd111_11 || ''}
              onChange={e => onDetailChange(selectedIdx, 'fd111_11', e.target.value)}
              disabled={!isEditing} />
          </div>

          <div className="form-grid">
            <label>產品名稱</label>
            <textarea className="form-textarea" value={selected.fd111_12 || ''} readOnly disabled rows={2} />

            <label>產品規格</label>
            <textarea className="form-textarea" value={selected.fd111_51 || ''}
              onChange={e => onDetailChange(selectedIdx, 'fd111_51', e.target.value)}
              disabled={!isEditing} rows={2} />
          </div>

          <div className="form-grid">
            <label>加工別</label>
            <textarea className="form-textarea" value={selected.fd111_52 || ''}
              onChange={e => onDetailChange(selectedIdx, 'fd111_52', e.target.value)}
              disabled={!isEditing} rows={2} />

            <label>預定交期</label>
            <input className="form-input" type="date" value={dateToInput(selected.fd111_19)}
              onChange={e => onDetailChange(selectedIdx, 'fd111_19', inputToDate(e.target.value))}
              disabled={!isEditing} />
          </div>

          <div className="form-grid">
            <label>採購數量</label>
            <input className="form-input" type="number" value={selected.fd111_15 ?? 0}
              onChange={e => onDetailChange(selectedIdx, 'fd111_15', parseFloat(e.target.value) || 0)}
              disabled={!isEditing} />
            <label>單位</label>
            <input className="form-input" value={selected.fd111_16 || ''} disabled readOnly />
            <label>採購單價</label>
            <input className="form-input" type="number" step="0.01" value={selected.fd111_17 ?? 0}
              onChange={e => onDetailChange(selectedIdx, 'fd111_17', parseFloat(e.target.value) || 0)}
              disabled={!isEditing} />
          </div>

          <div className="form-grid">
            <label>ASSORT</label>
            <Select value={selected.fd111_29 || 'N'}
              onChange={v => onDetailChange(selectedIdx, 'fd111_29', v)}
              disabled={!isEditing} style={{ width: 100 }}
              options={[
                { value: 'N', label: 'N (一般)' },
                { value: 'Y', label: 'Y (分色)' },
              ]} />

            <label>允收比率(%)</label>
            <input className="form-input" type="number" step="0.01" value={selected.fd111_301 ?? 0}
              onChange={e => onDetailChange(selectedIdx, 'fd111_301', parseFloat(e.target.value) || 0)}
              disabled={!isEditing} />
            <label>允收量</label>
            <input className="form-input" type="number" value={selected.fd111_302 ?? 0}
              onChange={e => onDetailChange(selectedIdx, 'fd111_302', parseFloat(e.target.value) || 0)}
              disabled={!isEditing} />
          </div>

          <div className="form-grid">
            <label>允收增加量</label>
            <input className="form-input" value={selected.fd111_30 ?? 0} disabled readOnly />

            <label>工廠布號</label>
            <input className="form-input" value={selected.fd111_28 || ''}
              onChange={e => onDetailChange(selectedIdx, 'fd111_28', e.target.value)}
              disabled={!isEditing} />
          </div>

          <div className="form-grid">
            <label>針孔內(幅寬)</label>
            <input className="form-input" value={selected.fd111_53 || ''}
              onChange={e => onDetailChange(selectedIdx, 'fd111_53', e.target.value)}
              disabled={!isEditing} />
            <label>針孔外(幅寬)</label>
            <input className="form-input" value={selected.fd111_54 || ''}
              onChange={e => onDetailChange(selectedIdx, 'fd111_54', e.target.value)}
              disabled={!isEditing} />
          </div>

          <div className="form-grid">
            <label>碼重</label>
            <input className="form-input" value={selected.fd111_55 || ''}
              onChange={e => onDetailChange(selectedIdx, 'fd111_55', e.target.value)}
              disabled={!isEditing} />
            <label>米平方重</label>
            <input className="form-input" value={selected.fd111_56 || ''}
              onChange={e => onDetailChange(selectedIdx, 'fd111_56', e.target.value)}
              disabled={!isEditing} />
          </div>

          <div className="form-grid">
            <label>應付單價</label>
            <input className="form-input" type="number" step="0.01" value={selected.fd111_26 ?? 0}
              onChange={e => onDetailChange(selectedIdx, 'fd111_26', parseFloat(e.target.value) || 0)}
              disabled={!isEditing} />
            <label>折讓單價</label>
            <input className="form-input" type="number" step="0.01" value={selected.fd111_25 ?? 0}
              onChange={e => onDetailChange(selectedIdx, 'fd111_25', parseFloat(e.target.value) || 0)}
              disabled={!isEditing} />
          </div>

          {/* Read-only cumulative fields */}
          <div className="form-grid readonly-summary">
            <label>累計交貨量</label>
            <span>{fmt(selected.fd111_20 as number | undefined)}</span>
            <label>累計驗貨數量</label>
            <span>{fmt(selected.fd111_21 as number | undefined)}</span>
            <label>累計確認數量</label>
            <span>{fmt(selected.fd111_22 as number | undefined)}</span>
          </div>
        </div>
      )}
    </div>
  );
});

Tab2Detail.displayName = 'Tab2Detail';
export default Tab2Detail;
